import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Shield, CheckCircle2, AlertTriangle, Eye, EyeOff, Mail } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";

const getRecoveryParams = () => {
  const query = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));

  return {
    code: query.get("code"),
    type: query.get("type") ?? hash.get("type"),
    accessToken: hash.get("access_token"),
    refreshToken: hash.get("refresh_token"),
    error: query.get("error_description") ?? hash.get("error_description") ?? query.get("error") ?? hash.get("error"),
  };
};

const cleanRecoveryUrl = () => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [canUpdatePassword, setCanUpdatePassword] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let mounted = true;
    const params = getRecoveryParams();
    const hasRecoveryIntent = params.type === "recovery" || Boolean(params.code || params.accessToken);

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === "PASSWORD_RECOVERY" || (hasRecoveryIntent && session)) {
        setCanUpdatePassword(true);
        setChecking(false);
      }
    });

    const initializeRecovery = async () => {
      try {
        if (params.error) {
          setLinkError(params.error.replace(/\+/g, " "));
          return;
        }

        if (params.accessToken && params.refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: params.accessToken,
            refresh_token: params.refreshToken,
          });
          if (error) throw error;
          setCanUpdatePassword(true);
          cleanRecoveryUrl();
          return;
        }

        const { data: sessionData } = await supabase.auth.getSession();
        if (hasRecoveryIntent && sessionData.session) {
          setCanUpdatePassword(true);
          cleanRecoveryUrl();
          return;
        }

        if (params.code) {
          const { error } = await supabase.auth.exchangeCodeForSession(params.code);
          if (error) throw error;
          setCanUpdatePassword(true);
          cleanRecoveryUrl();
        }
      } catch (error) {
        setLinkError(error instanceof Error ? error.message : "This reset link is invalid or has expired.");
      } finally {
        if (mounted) setChecking(false);
      }
    };

    initializeRecovery();

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const handleSendResetLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setSubmitting(false);

    if (error) {
      toast.error(error.message);
      return;
    }

    setEmailSent(true);
    setLinkError("");
    toast.success("Password reset email sent. Check your inbox.");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canUpdatePassword) {
      toast.error("Please open the reset link from your email first");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setDone(true);
    toast.success("Password updated. You're signed in.");
    setTimeout(() => navigate("/academy"), 1500);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-24 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Reset your password
            </CardTitle>
            <CardDescription>
              Choose a new password for your QuantumBox AI Academy account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {checking ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : done ? (
              <Alert className="border-primary/30 bg-primary/10">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <AlertDescription>
                  Password updated successfully. Redirecting to your Academy…
                </AlertDescription>
              </Alert>
            ) : !canUpdatePassword ? (
              <form onSubmit={handleSendResetLink} className="space-y-4">
                {linkError && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{linkError}. Please request a new reset link.</AlertDescription>
                  </Alert>
                )}

                {emailSent ? (
                  <Alert className="border-primary/30 bg-primary/10">
                    <Mail className="h-4 w-4 text-primary" />
                    <AlertDescription>
                      If an Academy account exists for that email, a reset link has been sent.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="reset-email">Email address</Label>
                      <Input
                        id="reset-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="admin@quantumbox.ai"
                        autoComplete="email"
                        className="mt-1"
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…
                        </>
                      ) : (
                        "Send reset link"
                      )}
                    </Button>
                  </>
                )}

                <Button type="button" variant="outline" className="w-full" onClick={() => navigate("/academy")}>
                  Back to Academy
                </Button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="password">New password</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="password"
                      type={show ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                      required
                      minLength={8}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setShow((s) => !s)}
                      aria-label={show ? "Hide password" : "Show password"}
                    >
                      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm">Confirm new password</Label>
                  <Input
                    id="confirm"
                    type={show ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter your new password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    className="mt-1"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Updating…
                    </>
                  ) : (
                    "Update password"
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
